import React, {
  useEffect,
  useLayoutEffect,
  useCallback,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Actions from 'redux/actions';
import { Form, message } from 'antd';

import { useHistory, useLocation } from 'react-router-dom';
import qs from 'qs';
import { RecaptchaVerifier, confirmPasswordReset } from 'firebase/auth';

import SuspenseWrapper from 'HOC/SuspenseWrapper';
import ResetPasswordModal from 'components/Modals/ResetPasswordModal';
import ConfirmEmailModal from 'components/Modals/ConfirmEmailModal';
import { MODAL_NAMES } from 'components/Modals/config';
import { useFirebase } from 'modules/shared/firebase/hooks/useFirebase';
import { ROUTING } from 'config';
import { errorAuthHandler, getFirebaseErrorMessage } from 'utils/messages';

export const withAuthorizationData = (WrappedComponent) => {
  const { firebase } = useFirebase();

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const [form] = Form.useForm();

  const { next, oobCode, continueUrl } = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  });

  const [modalKey, setModalKey] = useState('');

  const { error, loadingType, isTwoFactorAuth } = useSelector(
    (state) => state.auth,
  );

  useEffect(() => {
    errorAuthHandler(form, error);
  }, [error, form]);

  useLayoutEffect(() => {
    const auth = firebase.getAuth();
    window.recaptchaVerifier = new RecaptchaVerifier(
      auth,
      'recaptcha-container',
      {
        size: 'invisible',
      },
    );

    window.recaptchaVerifier.render();
  }, [firebase]);

  const onToggleModal = (key) => () => {
    setModalKey(modalKey !== key ? key : undefined);
  };

  const onLogin = useCallback(
    (data) => {
      dispatch(
        Actions.logIn.request({
          data,
          onRedirect: () => {
            history.push(next || ROUTING.DASHBOARD);
          },
        }),
      );
    },
    [dispatch, history, next],
  );

  const onCancelTwoFactorAuth = useCallback(() => {
    dispatch(Actions.changeIsTwoFactorAuth(false));
  }, [dispatch]);

  const onTwoFactorAuth = useCallback(
    (data) => {
      dispatch(
        Actions.twoFactorAuth.request({
          data,
          onRedirect: () => {
            history.push(next || ROUTING.DASHBOARD);
          },
        }),
      );
    },
    [dispatch, history],
  );

  const onResetPassword = useCallback(
    (data) => {
      dispatch(
        Actions.sendLinkToEmailForResetPassword.request({
          data,
          onModalShow: onToggleModal(MODAL_NAMES.RESET_PASSWORD),
        }),
      );
    },
    [dispatch, onToggleModal],
  );

  const onNewPassword = useCallback(
    (data) => {
      const auth = firebase.getAuth();

      confirmPasswordReset(auth, oobCode, data.password)
        .then((resp) => {
          message.success('Password has been changed');
          setTimeout(() => history.push(continueUrl), 3000);
        })
        .catch((error) => {
          const errorMessage = getFirebaseErrorMessage(error);
          message.error(errorMessage, 10);
        });
    },
    [firebase, oobCode, continueUrl, history],
  );

  const onFinish =
    (onAction) =>
    async ({ password, newPassword, email, code }) => {
      const captchaExclusionEmails = JSON.parse(
        process.env.REACT_APP_CAPTCHA_EXCLUSION_EMAILS,
      );

      const skipCaptchaVerification = captchaExclusionEmails.includes(
        email?.toLowerCase(),
      );

      if (
        skipCaptchaVerification ||
        (await window.recaptchaVerifier.verify())
      ) {
        onAction({
          email,
          password: password || newPassword,
          code,
        });
      }
    };

  return (
    <SuspenseWrapper>
      <WrappedComponent
        form={form}
        error={error}
        isLoading={!!loadingType}
        isTwoFactorAuth={isTwoFactorAuth}
        onLogin={onLogin}
        onTwoFactorAuth={onTwoFactorAuth}
        onCancelTwoFactorAuth={onCancelTwoFactorAuth}
        onResetPassword={onResetPassword}
        onNewPassword={onNewPassword}
        onFinish={onFinish}
      />
      <div id="recaptcha-container"></div>
      <ResetPasswordModal
        isOpen={modalKey === MODAL_NAMES.RESET_PASSWORD}
        onModalCancel={onToggleModal(MODAL_NAMES.RESET_PASSWORD)}
      />
      <ConfirmEmailModal
        isOpen={modalKey === MODAL_NAMES.CONFIRM_EMAIL}
        onModalCancel={onToggleModal(MODAL_NAMES.CONFIRM_EMAIL)}
        title="Email confirmation link sent successfully."
        description="Go to e-mail and click on the link for further registration."
      />
    </SuspenseWrapper>
  );
};

withAuthorizationData.propTypes = {
  WrappedComponent: PropTypes.node,
};

export default withAuthorizationData;
